import React, { ReactElement, ReactNode, useCallback, useRef, useState } from 'react';
import { WizardStep } from '../../types/WizardSteps';
import { useWizardMultistep } from './useWizardMultistep';

type ListenerAction = 'navigateNext' | 'navigateBack' | 'navigateFinal';

type Listener = {
    action: ListenerAction;
    listener: () => boolean | Promise<boolean>;
};

export type WizardNavigationValue = {
    navigateNext: (id: WizardStep) => void | Promise<void>;
    navigateBack: () => void;
    registerListener: (
        id: WizardStep,
        listenerAction: ListenerAction,
        fn: () => boolean | Promise<boolean>,
    ) => { remove: () => void };
    getNavigateNextHandler: (id: WizardStep) => (id: WizardStep) => void | Promise<void>;
    getNavigateBackHandler: (id: WizardStep) => () => void | Promise<void>;
    nextButtonLabel: string;
    setNextButtonLabel: (val: string) => void;
    callFinal: boolean;
    setCallFinal: (val: boolean) => void;
};

export const WizardNavigationContext = React.createContext<WizardNavigationValue | null>(null);

function WizardNavigationProvider({ children }: { children: ReactNode }): ReactElement<'div'> | null {
    const { jumpToStep, goToPreviousStep } = useWizardMultistep();
    const [nextButtonLabel, setNextButtonLabel] = useState('Dále');
    const [callFinal, setCallFinal] = useState(false);

    const listeners = useRef<Partial<Record<WizardStep, Array<Listener>>>>({});

    const registerListener = useCallback(
        (id: WizardStep, listenerAction: ListenerAction, fn: () => boolean | Promise<boolean>) => {
            const listener = {
                action: listenerAction,
                listener: fn,
            };
            const actionListeners = listeners.current[id] || [];
            actionListeners.push(listener);
            listeners.current[id] = actionListeners;

            return {
                remove: () => {
                    const actionListeners = listeners.current[id];
                    if (!actionListeners) {
                        return;
                    }
                    const nextListeners = [...actionListeners].filter(({ action }) => action !== listenerAction);
                    if (nextListeners.length) {
                        listeners.current[id] = nextListeners;
                    } else {
                        delete listeners.current[id];
                    }
                },
            };
        },
        [],
    );

    const navigateNext = useCallback(
        (id: WizardStep) => {
            jumpToStep(id);
        },
        [jumpToStep],
    );

    const navigateBack = useCallback(() => {
        goToPreviousStep();
    }, [goToPreviousStep]);

    const createNavigationHandler = useCallback(
        (action: ListenerAction, defaultHandler: (nextStepId?: WizardStep) => void) => (currentStepId: WizardStep) => {
            const listener = (listeners.current[currentStepId] || []).find((listener) => listener.action === action);
            if (!listener) {
                return defaultHandler;
            }

            // some wizard step registered a listener for navigation action
            return (nextStepId?: WizardStep) => {
                /* 
                      listener can by sync or async, we always return promise, which gets resolved immediately after the sync or async action completes
                    */
                return new Promise<void>((res, rej) => {
                    const result = listener.listener();
                    if (typeof result === 'boolean') {
                        // listener is sync action that returns boolean
                        const navigationHandled = result;
                        // navigation was not handled
                        if (!navigationHandled) {
                            defaultHandler(nextStepId);
                        }
                        res();
                        return;
                    } else if (typeof result === 'object' && result !== null && 'then' in result) {
                        // listener is async action, await it and resolve after it completes
                        result
                            .then((navigationHandled) => {
                                /* when true is returned it means the navigation was handled by listener */
                                if (!navigationHandled) {
                                    defaultHandler(nextStepId);
                                }
                                res();
                            })
                            .catch(rej);
                    } else {
                        // always ensure we resolve
                        defaultHandler(nextStepId);
                        res();
                    }
                });
            };
        },
        [],
    );

    const getNavigateNextHandler = useCallback(
        createNavigationHandler('navigateNext', (nextStepId) => {
            if (nextStepId) {
                navigateNext(nextStepId);
            }
        }),
        [navigateNext],
    );
    const getNavigateBackHandler = useCallback(createNavigationHandler('navigateBack', navigateBack), [navigateBack]);
    // const getNavigateNextHandler = useCallback(
    //     (currentStepId: WizardStep) => {
    //         if (listeners.current[currentStepId]?.action === 'navigateNext') {
    //             // some wizard step registered a listener for navigate next action
    //             return (nextStepId: WizardStep) => {
    //                 /*
    //                   listener can by sync or async, we always return promise, which gets resolved immediately after the sync or async action completes
    //                 */
    //                 return new Promise<void>((res, rej) => {
    //                     const result = listeners.current[currentStepId]?.listener();
    //                     if (typeof result === 'boolean') {
    //                         // listener is sync action that returns boolean
    //                         const navigationHandled = result;
    //                         // navigation was not handled
    //                         if (!navigationHandled) {
    //                             navigateNext(nextStepId);
    //                         }
    //                         res();
    //                         return;
    //                     } else if (typeof result === 'object' && result !== null && 'then' in result) {
    //                         // listener is async action, await it and resolve after it completes
    //                         result
    //                             .then((navigationHandled) => {
    //                                 /* when true is returned it means the navigation was handled by listener */
    //                                 if (!navigationHandled) {
    //                                     navigateNext(nextStepId);
    //                                 }
    //                                 res();
    //                             })
    //                             .catch(rej);
    //                     } else {
    //                         // always ensure we resolve
    //                         navigateNext(nextStepId);
    //                         res();
    //                     }
    //                 });
    //             };
    //         }
    //         return navigateNext;
    //     },
    //     [navigateNext],
    // );

    return (
        <WizardNavigationContext.Provider
            value={{
                navigateBack,
                navigateNext,
                registerListener,
                getNavigateNextHandler,
                getNavigateBackHandler,
                nextButtonLabel,
                setNextButtonLabel,
                callFinal,
                setCallFinal,
            }}
        >
            {children}
        </WizardNavigationContext.Provider>
    );
}

export { WizardNavigationProvider };
